// Library methods
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Components
import ExpandDevicesModal from "../../../components/UI/ExpandDevicesModal";
import DevicesCard from "../../../components/UI/DevicesCard";
import Loader from "../../../components/UI/Loader";
import DevicesListHeader from "../../../components/UI/DevicesListHeader";

// Utilities
import useClinic from "../../../hooks/useClinic";
import { getComparator } from "../../../utils/tableHelper";
import { useDevices } from "../../../hooks/useDevices";

const DevicesList = ({
  rows,
  isLoading,
  patients,
  devicesWithExams,
  connection,
}) => {
  // internationalization
  const { t } = useTranslation();

  // clinic context
  const { clinicId, clinicTimezone } = useClinic();

  const {
    unlinkLoading,
    linkLoading,
    unlinkDeviceModal,
    linkDeviceModal,
    unlinkSuccessMessage,
    unlinkFailureMessage,
    linkSuccessMessage,
    linkFailureMessage,
    handleUnlinkDeviceClick,
    handleLinkDeviceClick,
    checkIfDeviceHasExamInProgress,
  } = useDevices(connection, clinicId, patients, devicesWithExams);

  // states init
  const [isActive, setIsActive] = useState("all");
  const [expandDevicesModal, setExpandDevicesModal] = useState(false);

  // handle on close of expand devices modal
  const handleExpandDevicesClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setExpandDevicesModal(false);
    }
  };

  const sortedDevices = useMemo(() => {
    const order = "asc";
    const orderBy = ["alias"];
    return rows.sort(getComparator(order, orderBy));
  }, [rows]);

  const selectedDevices = useMemo(() => {
    if (isActive === "linked") {
      return sortedDevices?.filter((row) => row?.associatedExam);
    } else if (isActive === "unlinked") {
      return sortedDevices?.filter((row) => !row?.associatedExam);
    }
    return sortedDevices;
  }, [isActive, sortedDevices]);

  const ListElement = () =>
    isLoading ? (
      <Loader containerHeight="250px" />
    ) : (
      <Grid
        container
        display="flex"
        gap="20px"
        pb="16px"
        mb="16px"
        sx={{
          overflowX: "auto",
          flexWrap: "nowrap",
        }}
      >
        {sortedDevices.length > 0 ? (
          selectedDevices?.map((row) => {
            return (
              <DevicesCard
                key={row.id}
                row={row}
                handleLinkDeviceClick={handleLinkDeviceClick}
                handleUnlinkDeviceClick={handleUnlinkDeviceClick}
                isExamInProgress={checkIfDeviceHasExamInProgress(row.id)}
                devicesWithExams={devicesWithExams}
                unlinkLoading={unlinkLoading}
                linkLoading={linkLoading}
                clinicTimezone={clinicTimezone}
              />
            );
          })
        ) : (
          <Box my={8} width="100%" display="flex" justifyContent="center">
            <Typography
              fontSize="20px"
              fontWeight={600}
              sx={{ textTransform: "capitalize" }}
            >
              {t("word_no_devices")}
            </Typography>
          </Box>
        )}
      </Grid>
    );

  const ListElementExpanded = () =>
    isLoading ? (
      <Loader containerHeight="250px" />
    ) : (
      <Grid
        container
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        gap="20px"
      >
        {sortedDevices.length > 0 ? (
          sortedDevices.map((row) => {
            return (
              <DevicesCard
                key={row.id}
                row={row}
                handleLinkDeviceClick={handleLinkDeviceClick}
                handleUnlinkDeviceClick={handleUnlinkDeviceClick}
                isExamInProgress={checkIfDeviceHasExamInProgress(row.id)}
                devicesWithExams={devicesWithExams}
                unlinkLoading={unlinkLoading}
                linkLoading={linkLoading}
                clinicTimezone={clinicTimezone}
              />
            );
          })
        ) : (
          <Box my={8} width="100%" display="flex" justifyContent="center">
            <Typography
              fontSize="20px"
              fontWeight={600}
              sx={{ textTransform: "capitalize" }}
            >
              {t("word_no_devices")}
            </Typography>
          </Box>
        )}
      </Grid>
    );

  return (
    <Box px="24px">
      {rows?.length > 0 && (
        <DevicesListHeader
          rows={rows}
          isActive={isActive}
          setIsActive={setIsActive}
          setExpandDevicesModal={() => setExpandDevicesModal(true)}
          containerProps={{ py: "16px" }}
        />
      )}

      {ListElement()}

      {/* Unlink device exam modal */}
      {unlinkDeviceModal}

      {/* Link device exam modal */}
      {linkDeviceModal}

      {/* Expand devices modal */}
      <ExpandDevicesModal
        open={expandDevicesModal}
        onClose={handleExpandDevicesClose}
        onCancel={handleExpandDevicesClose}
      >
        {ListElementExpanded()}
      </ExpandDevicesModal>

      {/* Unlink device success/failure toasts */}
      {unlinkSuccessMessage}
      {unlinkFailureMessage}
      {linkSuccessMessage}
      {linkFailureMessage}
    </Box>
  );
};

export default DevicesList;
